import BaseService from '../../base/base_service';
import _ from '../../utils/underscore';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import {Check, TimeSpan} from "@sparkware/app-utils";
import Store from '../../utils/store';
import {TokenType} from "../../../../common/const/token_type";
import MIGRATION from "../../../../common/const/mrg_migration_consts";


const EMPTY = '';

class QueryStringDetectorService extends BaseService {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'QueryStringDetectorService';

    _handlersMap = null;

    /**
     * Initialize query string detector service.
     *
     * @param params
     */
    init(params) {
        super.init(params);
        this.onPageLoadCGP();
    }


    /**
     * On page load cgp for handling.
     *
     * @returns {QueryStringDetectorService}
     */
    onPageLoadCGP() {
        this.onSystemEvent(SYSTEM_EVENTS.ON_ORBIT_INIT_COMPLETE, () => {
            let queryStringParams = _.getQueryParameters();
            Object.keys(queryStringParams).forEach((queryParamName) => {
                if (EMPTY === queryParamName) {
                    return;
                }

                let handler = this._getQueryHandler(queryParamName);

                if (_.isFunction(handler)) {
                    handler.call(this, queryStringParams);
                }
            });

            // we subscribe to this only after all handlers run becasue if we have autologin it will logout and login the user.
            this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
                if(!this.serviceFactory.oAuth2Service().isAuthorized()) {
                    this._clearStore();
                }
            });
        });

        return this;
    }

    isInAutologin() {
        return !_.isEmpty(this._getTokenWithType(_.getQueryParameters())?.token);
    }

    _initializeHandlersMap() {
        this._handlersMap = {
            [this._autologinParameterName()]: this.tokenHandler,
            [MIGRATION.MIGRATION_TOKEN_QUERY_PARAM]: this.tokenHandler,
            reason: this.reasonHandler,
            sourcesubbrand: this.sourceSubBrandHandler,
            linkid: this.linkIDHandler,
            sourceproductpackage: this.sourceProductPackageHandler,
            clientversion:this.clientVersionHandler,
            specificnavigation: this.specificNavigationHandler
        };
    }

    /**
     *
     * @param queryParamName
     * @returns {*}
     * @private
     */
    _getQueryHandler(queryParamName) {
        queryParamName = queryParamName.toLowerCase();

        if(!this._handlersMap) {
            this._initializeHandlersMap();
        }

        return this._handlersMap[queryParamName];
    }

    _autologinParameterName() {
        return this.serviceFactory.oAuth2Service().getAutologinKey().toLowerCase();
    }

    /**
     * Reason handler.
     *
     * @param reason
     */
    reasonHandler(queryStringParams) {
        let reason = this._getQueryParamValue(queryStringParams,'reason');
        this.serviceFactory.gameReasonService().handleReason(reason);
    }

    _clearStore() {
        Store.del(['linkID', 'sourceProductPackage','sourceSubBrand', 'specificNavigation']);
    }

    _sendAnalytics(label, value) {
        let analyticsData = {
            category: '[Technical-event]',
            action: 'QueryDetector',
            label: label,
            value: value
        };

        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, analyticsData);
    }


    sourceProductPackageHandler(queryStringParams) {
        let sourceProductPackage = this._getQueryParamValue(queryStringParams, 'sourceProductPackage');
        Store.set('sourceProductPackage', sourceProductPackage);
    }

    clientVersionHandler(queryStringParams) {
        let clientVersion = this._getQueryParamValue(queryStringParams, 'ClientVersion');
        Store.set('ClientVersion', clientVersion);
    }

    sourceSubBrandHandler(queryStringParams) {
        let sourceSubBrand = this._getQueryParamValue(queryStringParams, 'sourceSubBrand');
        Store.set('sourceSubBrand', sourceSubBrand);
    }

    linkIDHandler(queryStringParams) {
        let linkID = this._getQueryParamValue(queryStringParams, 'linkID');
        Store.set('linkID', linkID);
    }

    _getAutologinToken(queryStringParams) {
        return this._getQueryParamValue(queryStringParams, this._autologinParameterName());
    }

    _getAutoLoginMigrationToken(queryStringParams) {
        return this._getQueryParamValue(queryStringParams, MIGRATION.MIGRATION_TOKEN_QUERY_PARAM);
    }

    _getTokenWithType(queryStringParams) {
        const migrationTokenValue = this._getAutoLoginMigrationToken(queryStringParams);

        if (!Check.isEmpty(migrationTokenValue)) {
            //We need to know whether the client was loaded with a migration token for analytics purposes.
            window.sessionStorage.setItem(MIGRATION.LOADED_WITH_MIGRATION_TOKEN_SESSION_STORAGE_KEY, "true");

            return {
                token: migrationTokenValue,
                tokenType: TokenType.MigrationToken
            };
        }

        return {
            token: this._getAutologinToken(queryStringParams),
            tokenType: TokenType.AuthenticationToken
        };
    }

    /**
     *
     * @param queryStringParams
     */
    tokenHandler(queryStringParams) {
        let {token, tokenType } = this._getTokenWithType(queryStringParams);
        let sessionTimerOffset = this._extractSessionTimerOffset(queryStringParams);
        let sourceProductPackage = this._getQueryParamValue(queryStringParams, 'sourceProductPackage');
        let brandId = this._getQueryParamValue(queryStringParams, 'brandId');
        let sourceSubBrandId = this._getQueryParamValue(queryStringParams, 'sourceSubBrand');
        let targetSubBrandId = this._getQueryParamValue(queryStringParams, 'targetSubBrand');

        this.serviceFactory.oAuth2Service().runAutoLogin(token, sessionTimerOffset, sourceProductPackage, brandId, sourceSubBrandId, targetSubBrandId, tokenType);
    }

    specificNavigationHandler(queryStringParams) {
        let specificNavigation = this._getQueryParamValue(queryStringParams, 'specificNavigation');

        Store.set('specificNavigation', specificNavigation);
    }

    getSourceSubBrand() {
        return Store.get('sourceSubBrand');
    }

    getExternalBrandCallbackURL() {
        if(!this._externalBrandCallbackURL) {
            this._parseSpecificNavigation();
        }

        return this._externalBrandCallbackURL;
    }

    getExternalBrandCallbackURLMethod() {
        if(!this._externalBrandCallbackURL) {
            this._parseSpecificNavigation();
        }

        return this._externalBrandCallbackURLMethod;
    }

    _parseSpecificNavigation() {
        let specificNavigationString = Store.get('specificNavigation');
        if(!specificNavigationString) {
            return;
        } else if (specificNavigationString.startsWith('http')) {
            this._externalBrandCallbackURL = specificNavigationString;
        } else {
            const specificNavigationDoc = _.parseXml(specificNavigationString);
            if (specificNavigationDoc) {
                const callbackUrlElems = specificNavigationDoc.getElementsByTagName("CallBackURL");
                if (callbackUrlElems && callbackUrlElems.length) {
                    this._externalBrandCallbackURL = callbackUrlElems.item(0).textContent;
                }

                const callbackMethodElems = specificNavigationDoc.getElementsByTagName("callbackMethod");
                if (callbackMethodElems && callbackMethodElems.length) {
                    this._externalBrandCallbackURLMethod = callbackMethodElems.item(0).textContent;
                }
            }
        }
    }

    _getQueryParamValue(queryStringParams, paramName) {
        paramName = paramName.toLowerCase();
        let keys = Object.keys(queryStringParams);

        for (let i = 0; i < keys.length; i++) {
            if (keys[i].toLowerCase() === paramName) {
                return queryStringParams[keys[i]];
            }
        }

        return null;
    }

    _extractSessionTimerOffset(queryStringParams) {
        let sessionTimerOffset = this._getQueryParamValue(queryStringParams, 'sessionTimer');
        try {
            if (sessionTimerOffset) {
                return TimeSpan.fromSeconds(sessionTimerOffset);
            }
        } catch (err) {
            this.logger.error(`Failed to parse sessionTimer from the query params! sessionTimer=${sessionTimerOffset}; Error: ${err}`);
        }

        return null;
    }
}


export default QueryStringDetectorService;